import { useTranslation } from 'react-i18next';

import type { EmailPlansData, EmailPlansHandlers } from '@ctct/application-layer';
import { PriceEstimatorDialog, WithScrollIntoView } from '@ctct/presentation-layer';
import type { EmailPlansHooks } from '@ctct/state-client-layer';

interface Props {
  data: EmailPlansData;
  handlers: EmailPlansHandlers;
  hooks: EmailPlansHooks;
  currency: string;
}

export function PricingTierDialog({
  data: { getPriceEstimatorDialogI18nKeys, getPriceTiersForPlan, getAllDisplayedPlans, getContactSalesLink },
  handlers,
  hooks,
  currency,
}: Props) {
  const { t } = useTranslation();
  const isVisible = hooks.useIsShowingEmailPricingTierDialog();
  const selectedPlanId = hooks.useSelectedEmailPlanId();
  const selectedPlan = getAllDisplayedPlans().find((plan) => plan.getId() === selectedPlanId);

  if (!isVisible || !selectedPlan) return null;

  const tierData = getPriceTiersForPlan(selectedPlan.getId());
  const i18nKeys = getPriceEstimatorDialogI18nKeys();

  return (
    <PriceEstimatorDialog
      i18nKeys={i18nKeys}
      onClose={handlers.onEmailPricingTierDialogClose}
      planType={selectedPlan.getType()}
    >
      {tierData.map((tier, index) => (
        <PriceEstimatorDialog.TierRow key={tier.tier} index={index}>
          <PriceEstimatorDialog.ContactsCell>
            {tier.tierMin}
            {tier.tierMax < 0 ? '+' : <> - {tier.tierMax}</>}
            {tier.current ? (
              <WithScrollIntoView>
                <span />
              </WithScrollIntoView>
            ) : null}
          </PriceEstimatorDialog.ContactsCell>
          <PriceEstimatorDialog.PricingCell>{tier.price.format(currency)}</PriceEstimatorDialog.PricingCell>
        </PriceEstimatorDialog.TierRow>
      ))}
      {/* render addtional table row with with contact sales link */}
      <PriceEstimatorDialog.TierRow key="custom-tier" index={tierData.length}>
        <PriceEstimatorDialog.ContactsCell>{tierData[tierData.length - 1].tierMax}+</PriceEstimatorDialog.ContactsCell>
        <PriceEstimatorDialog.PricingCell>
          <a href={getContactSalesLink()} target="_blank" rel="noopener noreferrer">
            {t(i18nKeys.contactSales)}
          </a>
        </PriceEstimatorDialog.PricingCell>
      </PriceEstimatorDialog.TierRow>
    </PriceEstimatorDialog>
  );
}
